/**
 * DISTRIBUTIONS (sponsor lifecycle). What each asset has returned, and to whom.
 *
 * Income-sharing is a promise on a listing until it is paid. This shows the Trust's distribution runs:
 * the gross income, the split to investors, and the sponsor's share, per period, so a sponsor can see
 * their asset performing rather than take it on faith. The Trust computes and pays; this is the record.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, LipaApiError, type Distributions as DistributionsData } from '@/lib/api';
import { PageHeader, SectionTitle, StatTile } from '@/components/dash';
import { StatusPill } from '@/components/status';
import { ErrorState, LoadingCard } from '@/components/states';
import { Reveal } from '@/components/motion';
import { fmtDate } from '@/lib/format';

export default function Distributions() {
  const [data, setData] = useState<DistributionsData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api.sponsor.distributions());
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not load distributions.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState body={error} onRetry={() => void load()} />;
  if (!data) return <LoadingCard lines={4} />;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Link
        to="/tokenize"
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
      >
        <BackIcon /> Tokenize
      </Link>

      <PageHeader title="Distributions" subtitle="Income your assets have paid out." />

      <div className="grid grid-cols-2 gap-3">
        <StatTile label="Paid to date" value={`KES ${data.paidToDate}`} accent />
        <StatTile label="Next run" value={fmtDate(data.nextRun)} />
      </div>

      <section>
        <SectionTitle>History</SectionTitle>
        <ul className="space-y-2.5">
          {data.history.map((d) => (
            <li key={d.id}>
              <Reveal>
                <Link
                  to={`/tokenize/assets/${d.assetId}`}
                  className="block rounded-card border border-border bg-surface p-4 shadow-card transition hover:border-primary/40"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ink">{d.asset}</p>
                      <p className="text-micro text-ink-subtle">
                        {d.period} · {d.kind}
                      </p>
                    </div>
                    <StatusPill status={d.status} />
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-3 border-t border-border pt-3">
                    <Mini label="Gross" value={`KES ${d.gross}`} />
                    <Mini label="To investors" value={`KES ${d.toInvestors}`} />
                    <Mini label="Your share" value={`KES ${d.toSponsor}`} accent />
                  </div>
                </Link>
              </Reveal>
            </li>
          ))}
        </ul>
      </section>

      <p className="rounded-card bg-surface-sunken p-3 text-center text-xs leading-relaxed text-ink-muted">
        The trust computes each distribution from the income the asset earned and the cap table,
        then pays investors and your share. This is the record of what it paid.
      </p>
    </div>
  );
}

function Mini({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="min-w-0">
      <p className="text-nano text-ink-subtle">{label}</p>
      <p className={`tabular truncate text-sm font-medium ${accent ? 'text-brand' : 'text-ink'}`}>
        {value}
      </p>
    </div>
  );
}

function BackIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 6l-6 6 6 6" />
    </svg>
  );
}
